import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon,
    Navigation, UserCheck, Radio, CalendarClock, MessageSquareQuote, Mic, Mail,
} from "lucide-react";

import ContentEditor from "./admin/tabs/ContentEditor.jsx";
import BrandingEditor from "./admin/tabs/BrandingEditor.jsx";
import FlightPathEditor from "./admin/tabs/FlightPathEditor.jsx";
import VideosEditor from "./admin/tabs/VideosEditor.jsx";
import BlogEditor from "./admin/tabs/BlogEditor.jsx";
import PilotLogEditor from "./admin/tabs/PilotLogEditor.jsx";
import BookingsManager from "./admin/tabs/BookingsManager.jsx";
import TestimonialsManager from "./admin/tabs/TestimonialsManager.jsx";
import ThingsEditor from "./admin/tabs/ThingsEditor.jsx";
import VoiceLogsModerator from "./admin/tabs/VoiceLogsModerator.jsx";
import NewsletterManager from "./admin/tabs/NewsletterManager.jsx";
import SubmissionsModerator from "./admin/tabs/SubmissionsModerator.jsx";
import SubscribersManager from "./admin/tabs/SubscribersManager.jsx";

const TABS = [
    { id: "content", label: "Site copy", icon: FileText, Comp: ContentEditor },
    { id: "branding", label: "Branding", icon: ImageIcon, Comp: BrandingEditor },
    { id: "flightpath", label: "Flight path", icon: Navigation, Comp: FlightPathEditor },
    { id: "videos", label: "Videos", icon: Film, Comp: VideosEditor },
    { id: "blog", label: "Blog", icon: BookOpen, Comp: BlogEditor },
    { id: "pilotlog", label: "Pilot's Log", icon: Radio, Comp: PilotLogEditor },
    { id: "bookings", label: "Bookings", icon: CalendarClock, Comp: BookingsManager },
    { id: "testimonials", label: "Testimonials", icon: MessageSquareQuote, Comp: TestimonialsManager },
    { id: "things", label: "Things to do", icon: MapPin, Comp: ThingsEditor },
    { id: "voicelogs", label: "Voice Logs", icon: Mic, Comp: VoiceLogsModerator },
    { id: "newsletter", label: "Newsletter", icon: Mail, Comp: NewsletterManager },
    { id: "submissions", label: "Submissions", icon: Inbox, Comp: SubmissionsModerator },
    { id: "subscribers", label: "Subscribers", icon: UserCheck, Comp: SubscribersManager },
];

export default function Admin() {
    const { user, loading } = useAuth();
    const [tab, setTab] = useState("content");

    if (loading) return <div className="px-6 py-32 text-center text-[var(--sb-text-dim)]">Loading…</div>;
    if (!user) return <Navigate to="/login?next=/admin" />;
    if (user.role !== "admin") return (
        <div className="max-w-xl mx-auto px-6 py-32 text-center">
            <h1 className="font-serif text-4xl tracking-tight mb-4">Admin only.</h1>
            <p className="text-[var(--sb-text-dim)]">This area is reserved for the site owner.</p>
        </div>
    );

    const ActiveTab = TABS.find((t) => t.id === tab)?.Comp;

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="admin-page">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
                <div>
                    <div className="overline mb-3">Admin</div>
                    <h1 className="font-serif text-4xl lg:text-6xl tracking-tighter leading-none">Studio.</h1>
                    <p className="text-[var(--sb-text-dim)] mt-3">Edit copy, videos, posts and curated picks. Changes are live immediately.</p>
                </div>
            </div>

            <div className="flex gap-2 mb-10 border-b border-[var(--sb-border)] overflow-x-auto" data-testid="admin-tabs">
                {TABS.map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wider whitespace-nowrap border-b-2 -mb-px transition-colors ${
                                tab === t.id ? "border-[var(--sb-brand)] text-[var(--sb-text)]" : "border-transparent text-[var(--sb-text-dim)] hover:text-[var(--sb-text)]"
                            }`}
                            data-testid={`admin-tab-${t.id}`}
                        >
                            <Icon size={14} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {ActiveTab && <ActiveTab />}
        </div>
    );
}
